import { ISelectItem } from '@lla-platform/core/core-ui'
import moment from 'moment'

export type CalendarMaxDate = 'Today' | 'Yesterday' | 'EndOFLastWeek'

export enum PeriodType {
  Today = 'Today',
  Yesterday = 'Yesterday',
  PreviousWeek = 'PreviousWeek',
  PreviousMonth = 'PreviousMonth',
  WeekToDate = 'WeekToDate',
  MonthToDate = 'MonthToDate',
  SixMonthToDate = 'SixMonthToDate',
  YearToDate = 'YearToDate',
  PreviousYear = 'PreviousYear',
  MoonLanding = 'MoonLanding',
  Custom = 'Custom',
  // Weekly mode periods
  Last4Weeks = 'Last4Weeks',
  Last6Weeks = 'Last6Weeks',
  // Monthly mode periods
  Last2Months = 'Last2Months',
  Last4Months = 'Last4Months',
  Last6Months = 'Last6Months'
}

export const PeriodTypeToLabel: { [key: string]: string } = {
  Today: 'Today',
  Yesterday: 'Yesterday',
  PreviousWeek: 'Last Week',
  PreviousMonth: 'Last Month',
  WeekToDate: 'Week To Date',
  MonthToDate: 'Month To Date',
  SixMonthToDate: 'Last 6 Months',
  YearToDate: 'Year To Date',
  PreviousYear: 'Last Year',
  MoonLanding: 'Moon Landing 🌙',
  Custom: 'Custom',
  // Weekly mode periods
  Last4Weeks: 'Last 4 Weeks',
  Last6Weeks: 'Last 6 Weeks',
  // Monthly mode periods
  Last2Months: 'Last 2 Months',
  Last4Months: 'Last 4 Months',
  Last6Months: 'Last 6 Months'
}

export const PeriodTypeSelectItem: ISelectItem[] = [
  { label: 'Today', value: PeriodType.Today },
  { label: 'Yesterday', value: PeriodType.Yesterday },
  { label: 'Last Week', value: PeriodType.PreviousWeek },
  { label: 'Last Month', value: PeriodType.PreviousMonth },
  { label: 'Week To Date', value: PeriodType.WeekToDate },
  { label: 'Month To Date', value: PeriodType.MonthToDate },
  { label: 'Last 6 Months', value: PeriodType.SixMonthToDate },
  { label: 'Year To Date', value: PeriodType.YearToDate },
  { label: 'Last Year', value: PeriodType.PreviousYear },
  { label: 'Moon Landing 🌙', value: PeriodType.MoonLanding },
  { label: 'Custom', value: PeriodType.Custom }
]

// Daily mode periods
export const PeriodTypeButtonsInfo = (): ISelectItem[] => [
  {
    label: 'Today',
    value: PeriodType.Today,
    extraInfo: {
      from: moment().utcOffset(0, true),
      to: moment().utcOffset(0, true)
    }
  },
  {
    label: 'Yesterday',
    value: PeriodType.Yesterday,
    extraInfo: {
      from: moment().subtract(1, 'day').utcOffset(0, true),
      to: moment().subtract(1, 'day').utcOffset(0, true)
    }
  },
  {
    label: 'Last Week',
    value: PeriodType.PreviousWeek,
    extraInfo: {
      from: moment().subtract(1, 'week').startOf('isoWeek').utcOffset(0, true),
      to: moment().subtract(1, 'week').endOf('isoWeek').utcOffset(0, true)
    }
  },
  {
    label: 'Last Month',
    value: PeriodType.PreviousMonth,
    extraInfo: {
      from: moment().subtract(1, 'month').startOf('month').utcOffset(0, true),
      to: moment().subtract(1, 'month').endOf('month').utcOffset(0, true)
    }
  },
  {
    label: 'Week To Date',
    value: PeriodType.WeekToDate,
    extraInfo: {
      from: moment().startOf('isoWeek').utcOffset(0, true),
      to: moment().utcOffset(0, true)
    }
  },
  {
    label: 'Month To Date',
    value: PeriodType.MonthToDate,
    extraInfo: {
      from: moment().startOf('month').utcOffset(0, true),
      to: moment().utcOffset(0, true)
    }
  },
  {
    label: 'Last 6 Months',
    value: PeriodType.SixMonthToDate,
    extraInfo: {
      from: moment().add(-5, 'month').startOf('month').utcOffset(0, true),
      to: moment().utcOffset(0, true)
    }
  },
  {
    label: 'Year To Date',
    value: PeriodType.YearToDate,
    extraInfo: {
      from: moment().startOf('year').utcOffset(0, true),
      to: moment().utcOffset(0, true)
    }
  },
  {
    label: 'Last Year',
    value: PeriodType.PreviousYear,
    extraInfo: {
      from: moment().subtract(1, 'year').startOf('year').utcOffset(0, true),
      to: moment().subtract(1, 'year').endOf('year').utcOffset(0, true)
    }
  },
  {
    label: 'Moon Landing 🌙',
    value: PeriodType.MoonLanding,
    extraInfo: {
      from: moment('1969-07-20').utcOffset(0, true),
      to: moment().utcOffset(0, true)
    }
  },
  { label: 'Custom', value: PeriodType.Custom }
]

// Weekly mode periods
export const WeeklyPeriodTypeButtonsInfo = (): ISelectItem[] => [
  {
    label: 'Last Week',
    value: PeriodType.PreviousWeek,
    extraInfo: {
      from: moment().subtract(1, 'week').startOf('isoWeek').utcOffset(0, true),
      to: moment().subtract(1, 'week').endOf('isoWeek').utcOffset(0, true)
    }
  },
  {
    label: 'Last 4 Weeks',
    value: PeriodType.Last4Weeks,
    extraInfo: {
      from: moment().subtract(4, 'weeks').startOf('isoWeek').utcOffset(0, true),
      to: moment().subtract(1, 'week').endOf('isoWeek').utcOffset(0, true)
    }
  },
  {
    label: 'Last 6 Weeks',
    value: PeriodType.Last6Weeks,
    extraInfo: {
      from: moment().subtract(6, 'weeks').startOf('isoWeek').utcOffset(0, true),
      to: moment().subtract(1, 'week').endOf('isoWeek').utcOffset(0, true)
    }
  },
  {
    label: 'Last Year',
    value: PeriodType.PreviousYear,
    extraInfo: {
      from: moment().subtract(1, 'year').startOf('year').utcOffset(0, true),
      to: moment().subtract(1, 'year').endOf('year').utcOffset(0, true)
    }
  },
  {
    label: 'Moon Landing 🌙',
    value: PeriodType.MoonLanding,
    extraInfo: {
      from: moment('1969-07-20').utcOffset(0, true),
      to: moment().utcOffset(0, true)
    }
  },
  { label: 'Custom', value: PeriodType.Custom }
]

// Monthly mode periods
export const MonthlyPeriodTypeButtonsInfo = (): ISelectItem[] => [
  {
    label: 'Last Month',
    value: PeriodType.PreviousMonth,
    extraInfo: {
      from: moment().subtract(1, 'month').startOf('month').utcOffset(0, true),
      to: moment().subtract(1, 'month').endOf('month').utcOffset(0, true)
    }
  },
  {
    label: 'Last 2 Months',
    value: PeriodType.Last2Months,
    extraInfo: {
      from: moment().subtract(2, 'months').startOf('month').utcOffset(0, true),
      to: moment().subtract(1, 'month').endOf('month').utcOffset(0, true)
    }
  },
  {
    label: 'Last 4 Months',
    value: PeriodType.Last4Months,
    extraInfo: {
      from: moment().subtract(4, 'months').startOf('month').utcOffset(0, true),
      to: moment().subtract(1, 'month').endOf('month').utcOffset(0, true)
    }
  },
  {
    label: 'Last 6 Months',
    value: PeriodType.Last6Months,
    extraInfo: {
      from: moment().subtract(6, 'months').startOf('month').utcOffset(0, true),
      to: moment().subtract(1, 'month').endOf('month').utcOffset(0, true)
    }
  },
  {
    label: 'Last Year',
    value: PeriodType.PreviousYear,
    extraInfo: {
      from: moment().subtract(1, 'year').startOf('year').utcOffset(0, true),
      to: moment().subtract(1, 'year').endOf('year').utcOffset(0, true)
    }
  },
  {
    label: 'Moon Landing 🌙',
    value: PeriodType.MoonLanding,
    extraInfo: {
      from: moment('1969-07-20').utcOffset(0, true),
      to: moment().utcOffset(0, true)
    }
  },
  { label: 'Custom', value: PeriodType.Custom }
]

export const TodayString = () => moment().utcOffset(0, true).format()
export const YesterdayString = () => moment().subtract(1, 'day').utcOffset(0, true).format()
export const EndOFLastWeekString = () =>
  moment().subtract(1, 'week').endOf('isoWeek').startOf('day').utcOffset(0, true).utc().format()
export const MinDateString = moment({ year: 2021 }).startOf('year').utcOffset(0, true).format()
